import { useState, useMemo } from "react";
import { GITHUB_REPOS, type GitHubRepo, type RepoCategory } from "../data/githubRepos";

interface CategoryMeta {
  id: "all" | RepoCategory;
  label: string;
  count: number;
}

export function RepoCatalog() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<"all" | RepoCategory>("all");
  const [visibility, setVisibility] = useState<"all" | "public" | "private">("all");
  const [onlyFeatured, setOnlyFeatured] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(12);

  const handleCategoryChange = (cat: "all" | RepoCategory) => {
    setCategory(cat);
    setVisibleLimit(12);
  };

  const handleVisibilityChange = (vis: "all" | "public" | "private") => {
    setVisibility(vis);
    setVisibleLimit(12);
  };

  const categories: CategoryMeta[] = useMemo(() => [
    { id: "all", label: "All Repositories", count: GITHUB_REPOS.length },
    { id: "ai-agents", label: "AI & Autonomous Agents", count: GITHUB_REPOS.filter((r) => r.category === "ai-agents").length },
    { id: "web-saas", label: "Web Applications & SaaS", count: GITHUB_REPOS.filter((r) => r.category === "web-saas").length },
    { id: "automation-crm", label: "CRM Automation & Integrations", count: GITHUB_REPOS.filter((r) => r.category === "automation-crm").length },
    { id: "infra-cloud", label: "Infrastructure & Edge Cloud", count: GITHUB_REPOS.filter((r) => r.category === "infra-cloud").length },
    { id: "tools-libraries", label: "Libraries & Developer Tools", count: GITHUB_REPOS.filter((r) => r.category === "tools-libraries").length },
  ], []);

  const filteredRepos = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GITHUB_REPOS.filter((repo) => {
      // Category filter
      if (category !== "all" && repo.category !== category) return false;
      // Visibility filter
      if (visibility === "public" && repo.isPrivate) return false;
      if (visibility === "private" && !repo.isPrivate) return false;
      // Featured filter
      if (onlyFeatured && !repo.featured) return false;

      // Text search
      if (!q) return true;
      const matchName = repo.name.toLowerCase().includes(q);
      const matchTitle = repo.title.toLowerCase().includes(q);
      const matchDesc = repo.description.toLowerCase().includes(q);
      const matchLang = repo.language?.toLowerCase().includes(q);
      const matchTags = repo.tags.some((tag) => tag.toLowerCase().includes(q));

      return matchName || matchTitle || matchDesc || matchLang || matchTags;
    });
  }, [query, category, visibility, onlyFeatured]);

  const handleRequestDemo = (repo: GitHubRepo) => {
    const bookingEl = document.getElementById("book-call") || document.getElementById("schedule");
    if (bookingEl) {
      bookingEl.scrollIntoView({ behavior: "smooth" });
    } else {
      const text = encodeURIComponent(`Hello Lucas! I am reviewing your GitHub repositories and would like to request an architectural briefing on ${repo.title} (${repo.name}).`);
      window.open(`https://wa.me/5521988407505?text=${text}`, "_blank");
    }
  };

  return (
    <div className="repo-catalog-wrapper" id="repos">
      {/* Metric summary strip */}
      <div className="repo-stats-strip">
        <div className="repo-stat-card">
          <span className="repo-stat-number">{GITHUB_REPOS.length}</span>
          <span className="repo-stat-label">Total GitHub Projects</span>
        </div>
        <div className="repo-stat-card">
          <span className="repo-stat-number">{GITHUB_REPOS.filter(r => !r.isPrivate).length}</span>
          <span className="repo-stat-label">Open Source Systems</span>
        </div>
        <div className="repo-stat-card">
          <span className="repo-stat-number">{GITHUB_REPOS.filter(r => r.isPrivate).length}</span>
          <span className="repo-stat-label">Enterprise &amp; Private Codebases</span>
        </div>
        <div className="repo-stat-card">
          <span className="repo-stat-number">5</span>
          <span className="repo-stat-label">Specialized Architecture Clusters</span>
        </div>
      </div>

      {/* Control bar: search, category filters, visibility toggles */}
      <div className="repo-controls-panel">
        <div className="repo-search-box">
          <span className="repo-search-icon">🔍</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by repository name, technology (Next.js, Python, MCP, GHL), tags..."
            aria-label="Search repositories"
          />
          {query && (
            <button
              type="button"
              className="repo-search-clear"
              onClick={() => setQuery("")}
              title="Clear search query"
            >
              ✕
            </button>
          )}
        </div>

        {/* Category tabs */}
        <div className="repo-category-tabs">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`repo-cat-tab ${category === cat.id ? "active" : ""}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              {cat.label}
              <span className="repo-cat-count">{cat.count}</span>
            </button>
          ))}
        </div>

        {/* Sub-filters: Visibility + Featured */}
        <div className="repo-subfilters">
          <div className="repo-visibility-group">
            <span className="subfilter-label">Visibility:</span>
            <button
              type="button"
              className={`repo-vis-btn ${visibility === "all" ? "active" : ""}`}
              onClick={() => handleVisibilityChange("all")}
            >
              All ({GITHUB_REPOS.length})
            </button>
            <button
              type="button"
              className={`repo-vis-btn ${visibility === "public" ? "active" : ""}`}
              onClick={() => handleVisibilityChange("public")}
            >
              🌐 Open Source (65)
            </button>
            <button
              type="button"
              className={`repo-vis-btn ${visibility === "private" ? "active" : ""}`}
              onClick={() => handleVisibilityChange("private")}
            >
              🔒 Enterprise / Private (103)
            </button>
          </div>

          <label className="repo-featured-toggle">
            <input
              type="checkbox"
              checked={onlyFeatured}
              onChange={(e) => {
                setOnlyFeatured(e.target.checked);
                setVisibleLimit(12);
              }}
            />
            <span>⭐ Starred Flagships Only</span>
          </label>
        </div>
      </div>

      {/* Results header */}
      <div className="repo-results-meta">
        <span>
          Showing <strong>{query ? filteredRepos.length : Math.min(visibleLimit, filteredRepos.length)}</strong> of <strong>{filteredRepos.length}</strong> repositories {filteredRepos.length !== GITHUB_REPOS.length && `(filtered from total of ${GITHUB_REPOS.length})`}
        </span>
        {query && (
          <span className="repo-query-tag">
            Filter: "{query}"
          </span>
        )}
      </div>

      {/* Repository Card Grid */}
      {filteredRepos.length === 0 ? (
        <div className="repo-empty-state">
          <h3>No repositories found</h3>
          <p>Try adjusting your search terms or clearing category filters.</p>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setQuery("");
              setCategory("all");
              setVisibility("all");
              setOnlyFeatured(false);
              setVisibleLimit(12);
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <>
          <div className="repo-grid">
            {(query ? filteredRepos : filteredRepos.slice(0, visibleLimit)).map((repo) => (
              <div
                key={repo.name}
                className={`repo-card ${repo.featured ? "repo-card-featured" : ""} ${repo.isPrivate ? "repo-card-private" : "repo-card-public"}`}
              >
                <div className="repo-card-header">
                  <div className="repo-card-title-group">
                    <div className="repo-card-badges-top">
                      {repo.featured && (
                        <span className="badge-featured" title="Automatrix Flagship Project">
                          ⭐ Flagship
                        </span>
                      )}
                      <span className={`badge-visibility ${repo.isPrivate ? "is-private" : "is-public"}`}>
                        {repo.isPrivate ? "🔒 Enterprise" : "🌐 Open Source"}
                      </span>
                      <span className="badge-category">
                        {repo.category.replace("-", " ")}
                      </span>
                    </div>
                    <h3 className="repo-card-title">{repo.title}</h3>
                    <code className="repo-card-slug">{repo.name}</code>
                  </div>
                </div>

                <p className="repo-card-desc">{repo.description}</p>

                {/* Tags */}
                <div className="repo-card-tags">
                  {repo.tags.map((tag) => (
                    <span key={tag} className="repo-tag-pill">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Card Footer */}
                <div className="repo-card-footer">
                  <div className="repo-card-tech">
                    {repo.language && (
                      <span className="repo-lang-badge">
                        <span className="repo-lang-dot" />
                        {repo.language}
                      </span>
                    )}
                    {repo.stars > 0 && (
                      <span className="repo-stars-badge">
                        ★ {repo.stars}
                      </span>
                    )}
                  </div>

                  <div className="repo-card-cta">
                    {repo.isPrivate ? (
                      <button
                        type="button"
                        className="btn-repo-private"
                        onClick={() => handleRequestDemo(repo)}
                        title="Request architectural walkthrough or access"
                      >
                        Request Access / Briefing
                      </button>
                    ) : (
                      <a
                        href={repo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-repo-public"
                      >
                        View on GitHub ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!query && visibleLimit < filteredRepos.length && (
            <div className="repo-load-more-wrap" style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "32px" }}>
              <button
                type="button"
                className="btn primary"
                onClick={() => setVisibleLimit((prev) => prev + 12)}
              >
                Load More (+12 Repositories)
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => setVisibleLimit(filteredRepos.length)}
              >
                Show All ({filteredRepos.length})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
